import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, 
  Calendar, 
  Users, 
  Search, 
  Eye, 
  CheckCircle, 
  X, 
  Loader2,
  MessageSquare,
  Filter,
  Send
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { isCoachingAdmin as checkIsCoachingAdmin } from '../../utils/rbac';
import { getBatches, getBatchEnrollments } from '../../firebase/collections';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  getDocs, 
  addDoc, 
  serverTimestamp, 
  doc, 
  updateDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

const GrievanceManagement = () => {
  const { currentUser, userProfile } = useRealtime();
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, resolved, closed
  const [typeFilter, setTypeFilter] = useState('all'); // all, complaint, suggestion, grievance
  
  // Grievances
  const [grievances, setGrievances] = useState([]);
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [showGrievanceModal, setShowGrievanceModal] = useState(false);
  const [replyText, setReplyText] = useState('');

  const isSuperAdmin = userProfile?.role === 'admin' || 
                       userProfile?.role === 'superadmin' || 
                       userProfile?.role === 'founder';
  const isCoachingAdmin = checkIsCoachingAdmin(userProfile, currentUser);
  
  // Only use coachingId if user is a coaching admin or staff, not a super admin
  const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);

  useEffect(() => {
    loadBatches();
  }, [coachingId]);

  useEffect(() => {
    if (selectedBatch) {
      loadEnrollments();
    }
  }, [selectedBatch]);

  const loadBatches = async () => {
    try {
      setLoading(true);
      const result = await getBatches(coachingId || null);
      if (result.success) {
        setBatches(result.data || []);
      }
    } catch (error) {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const loadEnrollments = async () => {
    if (!selectedBatch) return;
    try {
      const result = await getBatchEnrollments(selectedBatch.id);
      if (result.success) {
        const mappedEnrollments = (result.data || []).map(enrollment => ({
          ...enrollment,
          studentId: enrollment.userId || enrollment.id,
          studentName: enrollment.userData?.fullName || enrollment.userData?.name || enrollment.email || 'Unknown',
          studentEmail: enrollment.userData?.email || enrollment.email || ''
        }));
        setEnrollments(mappedEnrollments);
      }
    } catch (error) {
      toast.error('Failed to load enrollments');
    }
  };

  const loadGrievances = useCallback(() => {
    if (!selectedBatch) return;

    let unsubscribe = null;

    // Try composite query first (requires index)
    const tryCompositeQuery = () => {
      const q = query(
        collection(db, 'grievances'),
        where('batchId', '==', selectedBatch.id),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const grievancesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setGrievances(grievancesData);
      }, (error) => {
        console.error('Error loading grievances (composite query):', error);
        // If index error, try fallback query
        if (error?.code === 'failed-precondition' || 
            error?.message?.includes('index') || 
            error?.message?.includes('requires an index')) {
          console.log('Composite index not available, trying fallback query...');
          if (unsubscribe) unsubscribe();
          unsubscribe = tryFallbackQuery();
        } else {
          toast.error('Failed to load grievances. Please try refreshing the page.');
        }
      });
    };

    // Fallback query: query by batchId only, then sort client-side
    const tryFallbackQuery = () => {
      const fallbackQ = query(
        collection(db, 'grievances'),
        where('batchId', '==', selectedBatch.id)
      );

      return onSnapshot(fallbackQ, (snapshot) => {
        const grievancesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort client-side by createdAt descending
        grievancesData.sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt || 0);
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt || 0);
          return bTime - aTime;
        });
        setGrievances(grievancesData);
      }, (error) => {
        console.error('Error loading grievances (fallback query):', error);
        toast.error('Failed to load grievances. Please try refreshing the page.');
      });
    };

    // Start with composite query
    unsubscribe = tryCompositeQuery();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedBatch]);

  // Set up real-time listener after function is defined
  useEffect(() => {
    if (selectedBatch) {
      const unsubscribe = loadGrievances();
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [selectedBatch, loadGrievances]);

  const handleUpdateStatus = async (grievanceId, newStatus) => {
    try {
      await updateDoc(doc(db, 'grievances', grievanceId), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        resolvedAt: newStatus === 'resolved' ? serverTimestamp() : null,
        resolvedBy: newStatus === 'resolved' ? userProfile?.uid : null
      });
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSendReply = async () => {
    if (!selectedGrievance || !replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    try {
      const reply = {
        text: replyText,
        repliedBy: userProfile?.uid,
        repliedByName: userProfile?.fullName || userProfile?.email,
        repliedAt: serverTimestamp()
      };

      const currentReplies = selectedGrievance.replies || [];
      await updateDoc(doc(db, 'grievances', selectedGrievance.id), {
        replies: [...currentReplies, reply],
        updatedAt: serverTimestamp(),
        lastRepliedAt: serverTimestamp()
      });

      toast.success('Reply sent successfully');
      setReplyText('');
      setShowGrievanceModal(false);
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const filteredGrievances = grievances.filter(grievance => {
    const matchesSearch = grievance.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         grievance.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         grievance.studentName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (statusFilter !== 'all' && grievance.status !== statusFilter) return false;
    if (typeFilter !== 'all' && grievance.type !== typeFilter) return false;
    
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'complaint':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'suggestion':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'grievance':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
          <AlertTriangle className="w-8 h-8 mr-3 text-red-600" /> Grievance Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Manage student complaints, suggestions, and grievances</p>
      </div>

      {/* Batch Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Batch
        </label>
        <select
          value={selectedBatch?.id || ''}
          onChange={(e) => {
            const batch = batches.find(b => b.id === e.target.value);
            setSelectedBatch(batch);
          }}
          className="w-full md:w-1/3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
        >
          <option value="">Select a batch</option>
          {batches.map((batch) => (
            <option key={batch.id} value={batch.id}>
              {batch.name}
            </option>
          ))}
        </select>
      </div>

      {selectedBatch && (
        <>
          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search grievances..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="text-gray-400" size={20} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Types</option>
                  <option value="complaint">Complaint</option>
                  <option value="suggestion">Suggestion</option>
                  <option value="grievance">Grievance</option>
                </select>
              </div>
            </div>
          </div>

          {/* Grievances List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Grievances</h2>
              <div className="space-y-4">
                {filteredGrievances.map((grievance) => (
                  <div
                    key={grievance.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => {
                      setSelectedGrievance(grievance);
                      setShowGrievanceModal(true);
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {grievance.subject || 'No Subject'}
                          </h3>
                          <span className={`px-2 py-1 rounded text-xs ${getTypeColor(grievance.type)}`}>
                            {grievance.type?.charAt(0).toUpperCase() + grievance.type?.slice(1) || 'Unknown'}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(grievance.status)}`}>
                            {grievance.status?.charAt(0).toUpperCase() + grievance.status?.slice(1) || 'Pending'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {grievance.message?.substring(0, 150)}...
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          <span>{grievance.studentName}</span>
                          <span>•</span>
                          <span>
                            {grievance.createdAt?.toDate?.()?.toLocaleDateString() || '-'}
                          </span>
                          {grievance.replies && grievance.replies.length > 0 && (
                            <>
                              <span>•</span>
                              <span>{grievance.replies.length} reply(ies)</span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedGrievance(grievance);
                          setShowGrievanceModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredGrievances.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No grievances found.</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Grievance Detail Modal */}
      {showGrievanceModal && selectedGrievance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Grievance Details</h3>
              <button onClick={() => setShowGrievanceModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`px-2 py-1 rounded text-xs ${getTypeColor(selectedGrievance.type)}`}>
                    {selectedGrievance.type?.charAt(0).toUpperCase() + selectedGrievance.type?.slice(1) || 'Unknown'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedGrievance.status)}`}>
                    {selectedGrievance.status?.charAt(0).toUpperCase() + selectedGrievance.status?.slice(1) || 'Pending'}
                  </span>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {selectedGrievance.subject || 'No Subject'}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  From: {selectedGrievance.studentName} ({selectedGrievance.studentEmail})
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Date: {selectedGrievance.createdAt?.toDate?.()?.toLocaleString() || '-'}
                </p>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Message:</p>
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                  {selectedGrievance.message}
                </p>
              </div>

              {selectedGrievance.replies && selectedGrievance.replies.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Replies:</p>
                  <div className="space-y-3">
                    {selectedGrievance.replies.map((reply, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {reply.repliedByName || 'Admin'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {reply.repliedAt?.toDate?.()?.toLocaleString() || '-'}
                          </p>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{reply.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reply
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white mb-3"
                  placeholder="Type your reply here..."
                />
                <div className="flex justify-between items-center">
                  <div className="flex space-x-2">
                    {selectedGrievance.status !== 'resolved' && (
                      <button
                        onClick={() => handleUpdateStatus(selectedGrievance.id, 'resolved')}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center space-x-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Mark Resolved</span>
                      </button>
                    )}
                    {selectedGrievance.status !== 'closed' && (
                      <button
                        onClick={() => handleUpdateStatus(selectedGrievance.id, 'closed')}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
                      >
                        Close
                      </button>
                    )}
                  </div>
                  <button
                    onClick={handleSendReply}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center space-x-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>Send Reply</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrievanceManagement;

